import { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import DocumentLink from "@/components/DocumentLink";
import { FileText, CheckCircle2, XCircle, CalendarClock } from "lucide-react";
import { formatDate } from "@/lib/checklist";

export default function ReviewDialog({ open, onOpenChange, document, onReviewed }) {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [mode, setMode] = useState(null);
  const [photoUrl, setPhotoUrl] = useState(null);

  const reset = () => { setReason(""); setBusy(false); setMode(null); setPhotoUrl(null); };

  const isIdPhoto = document?.item_key === "id_photo";

  useEffect(() => {
    if (!open || !isIdPhoto || !document?.storage_path) {
      setPhotoUrl(null);
      return;
    }
    let cancelled = false;
    api.get(`/documents/${document.id}/signed-url`)
      .then(({ data }) => { if (!cancelled) setPhotoUrl(data.url); })
      .catch(() => { if (!cancelled) setPhotoUrl(null); });
    return () => { cancelled = true; };
  }, [open, isIdPhoto, document?.id, document?.storage_path]);

  const submit = async (action) => {
    if (action === "reject" && !reason.trim()) {
      toast.error("Rejection reason required");
      return;
    }
    setBusy(true);
    try {
      await api.post(`/documents/${document.id}/review`, { action, reason: action === "reject" ? reason.trim() : null });
      toast.success(action === "approve" ? "Document approved" : "Document rejected");
      reset();
      onOpenChange(false);
      onReviewed && onReviewed();
    } catch (e) {
      toast.error("Review failed", { description: formatApiErrorDetail(e.response?.data?.detail) || e.message });
    } finally {
      setBusy(false);
    }
  };

  if (!document) return null;
  const hasFile = !!document.storage_path;
  const meta = document.metadata || {};

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) reset(); onOpenChange(v); }}>
      <DialogContent className="bg-white sm:max-w-xl" data-testid="review-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">Review document</DialogTitle>
          <DialogDescription>
            {document.item_title || document.item_key} — uploaded by {document.uploaded_by_name || "worker"}
          </DialogDescription>
        </DialogHeader>

        <div className="rounded-lg border border-slate-200 bg-stone-50 p-4 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <div className="h-10 w-10 rounded-md bg-white border border-slate-200 flex items-center justify-center">
              <FileText className="h-5 w-5 text-slate-500" />
            </div>
            <div className="min-w-0">
              <div className="text-sm font-medium text-slate-900 truncate">
                {document.original_filename || "Data-only record"}
              </div>
              <div className="text-xs text-slate-500">
                {document.size ? `${(document.size / 1024).toFixed(1)} KB` : "—"} · {document.content_type || "—"}
              </div>
            </div>
          </div>
          {hasFile && (
            <DocumentLink docId={document.id} testid="review-open-file" />
          )}
        </div>

        {isIdPhoto && photoUrl && (
          <div className="mt-2 rounded-lg border border-slate-200 bg-slate-50 p-4 flex items-center justify-center">
            <img
              src={photoUrl}
              alt="ID photo submitted"
              className="max-h-72 rounded-md object-contain bg-white border border-slate-200 shadow-sm"
              data-testid="review-id-photo"
            />
          </div>
        )}

        {(document.expiry_date || meta.issue_date || meta.completed_date) && (
          <div className="rounded-md border border-slate-200 bg-white p-3 text-xs text-slate-600 space-y-1">
            {meta.issue_date && (<div><span className="font-semibold text-slate-800">Issued:</span> {formatDate(meta.issue_date)}</div>)}
            {meta.completed_date && (<div><span className="font-semibold text-slate-800">Completed:</span> {formatDate(meta.completed_date)}</div>)}
            {document.expiry_date && (
              <div className="flex items-center gap-1"><CalendarClock className="h-3 w-3" /><span className="font-semibold text-slate-800">Expires:</span> {formatDate(document.expiry_date)}</div>
            )}
          </div>
        )}

        {meta.citizenship === "non_uk" && (
          <div className="rounded-md border border-slate-200 bg-white p-3 text-xs text-slate-700 space-y-1">
            <div className="label-caps text-slate-500 mb-1">Non-UK Right to Work</div>
            <div><span className="font-semibold">Share code:</span> {meta.share_code}</div>
            <div><span className="font-semibold">DOB:</span> {meta.dob}</div>
            <div><span className="font-semibold">Visa expiry:</span> {formatDate(meta.visa_expiry)}</div>
          </div>
        )}

        {mode === "reject" && (
          <div className="mt-2 space-y-2">
            <label className="label-caps">Rejection reason</label>
            <Textarea data-testid="review-reason-input" value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Explain what needs fixing so the worker can re-upload." rows={4} />
          </div>
        )}

        <DialogFooter className="gap-2 sm:justify-between">
          {mode !== "reject" ? (
            <>
              <Button variant="outline" onClick={() => setMode("reject")} data-testid="review-reject-btn" className="border-rose-200 text-rose-700 hover:bg-rose-50 hover:text-rose-800">
                <XCircle className="h-4 w-4 mr-1.5" /> Reject
              </Button>
              <Button onClick={() => submit("approve")} disabled={busy} className="bg-emerald-600 hover:bg-emerald-700" data-testid="review-approve-btn">
                <CheckCircle2 className="h-4 w-4 mr-1.5" /> {busy ? "Working…" : "Approve"}
              </Button>
            </>
          ) : (
            <>
              <Button variant="outline" onClick={() => setMode(null)} data-testid="review-cancel-reject">Back</Button>
              <Button onClick={() => submit("reject")} disabled={busy || !reason.trim()} className="bg-rose-600 hover:bg-rose-700" data-testid="review-confirm-reject">
                {busy ? "Working…" : "Confirm rejection"}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
